import type { MetaFunction } from '@remix-run/node'
import { Link } from '@remix-run/react'
import { ArrowRight } from 'lucide-react'
import { buttonVariants } from '#app/components/ui/button'
import { cn } from '#app/utils/misc'
import { siteConfig } from '#app/utils/constants/brand'
import { ROUTE_PATH as LOGIN_PATH } from '#app/routes/auth+/login'

export const ROUTE_PATH = '/guides/inbound-email-webhook' as const

const TITLE = 'Inbound Email Webhook Tutorial — Parse Email to JSON | Inbox Bridge'
const DESCRIPTION =
  'Step-by-step tutorial: receive an email at a webhook endpoint, parse inbound email into JSON, handle retries, and test your integration with Inbox Bridge.'
const URL = `${siteConfig.siteUrl}/guides/inbound-email-webhook`

// Pinned manually so the dateModified only changes when the content actually does.
// Bump dateModified when you make substantive edits to the guide body.
const DATE_PUBLISHED = '2026-05-12'
const DATE_MODIFIED = '2026-07-20'

const ARTICLE_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Inbound email webhook tutorial',
  description: DESCRIPTION,
  image: siteConfig.siteImageUrl,
  url: URL,
  datePublished: DATE_PUBLISHED,
  dateModified: DATE_MODIFIED,
  author: {
    '@type': 'Organization',
    name: siteConfig.siteTitle,
    url: siteConfig.siteUrl,
  },
  publisher: {
    '@type': 'Organization',
    name: siteConfig.siteTitle,
    url: siteConfig.siteUrl,
    logo: { '@type': 'ImageObject', url: siteConfig.siteImageUrl },
  },
  mainEntityOfPage: { '@type': 'WebPage', '@id': URL },
}

const BREADCRUMB_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: siteConfig.siteUrl },
    { '@type': 'ListItem', position: 2, name: 'Inbound email webhook tutorial', item: URL },
  ],
}

export const meta: MetaFunction = () => {
  return [
    { title: TITLE },
    { name: 'description', content: DESCRIPTION },
    { tagName: 'link', rel: 'canonical', href: URL },
    { property: 'og:type', content: 'article' },
    { property: 'og:url', content: URL },
    { property: 'og:title', content: TITLE },
    { property: 'og:description', content: DESCRIPTION },
    { property: 'og:image', content: siteConfig.siteImageUrl },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: TITLE },
    { name: 'twitter:description', content: DESCRIPTION },
    { 'script:ld+json': ARTICLE_JSON_LD },
    { 'script:ld+json': BREADCRUMB_JSON_LD },
  ]
}

export default function InboundEmailWebhookGuide() {
  return (
    <main className="mx-auto flex w-full max-w-screen-md flex-col gap-8 px-6 py-20">
      <header className="flex flex-col gap-4">
        <p className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Guide
        </p>
        <h1 className="text-4xl font-bold tracking-tight text-primary md:text-5xl">
          Inbound email webhook tutorial
        </h1>
        <p className="text-lg leading-relaxed text-muted-foreground">
          Learn how to receive email as a webhook, parse the payload into JSON, and
          wire it up to your application in about five minutes.
        </p>
      </header>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">1. Create a bridge</h2>
        <p className="leading-relaxed text-muted-foreground">
          Sign up and create your first bridge. You&apos;ll get a unique inbound email
          address that looks like <code>abc123@webhook.inboxbridge.io</code>. Any email
          sent to this address will be parsed and forwarded to your webhook URL.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">
          2. Configure your webhook endpoint
        </h2>
        <p className="leading-relaxed text-muted-foreground">
          Point the bridge at any HTTPS endpoint that accepts POST requests. Inbox
          Bridge sends a JSON payload with the parsed message.
        </p>
        <pre className="overflow-x-auto rounded-lg border border-border bg-secondary/40 p-4 text-sm text-muted-foreground">
          {`{
  "from": "sender@example.com",
  "to": "abc123@webhook.inboxbridge.io",
  "subject": "Hello",
  "text": "Plain text body...",
  "html": "<p>HTML body...</p>",
  "headers": { "Message-ID": "..." },
  "attachments": [
    { "filename": "report.pdf", "contentType": "application/pdf", "size": 12345 }
  ]
}`}
        </pre>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">3. Handle the payload</h2>
        <p className="leading-relaxed text-muted-foreground">
          In your handler, read the JSON body and do whatever your app needs — create a
          ticket, file a lead, store an attachment. Return a 2xx status to acknowledge
          delivery.
        </p>
        <pre className="overflow-x-auto rounded-lg border border-border bg-secondary/40 p-4 text-sm text-muted-foreground">
          {`app.post('/inbound', async (req, res) => {
  const { from, subject, text, attachments } = req.body
  await db.tickets.create({ from, subject, body: text })
  res.sendStatus(200)
})`}
        </pre>
        <p className="leading-relaxed text-muted-foreground">
          For type-safe parsing and signature checks, use our open-source package,{' '}
          <a
            href="https://www.npmjs.com/package/inbound-email-webhook"
            target="_blank"
            rel="noreferrer"
            className="font-medium text-primary underline underline-offset-4 hover:no-underline">
            inbound-email-webhook
          </a>
          . It verifies the delivery and returns a fully typed email, so you skip the
          hand-written boilerplate. See the{' '}
          <Link
            to="/guides/receive-inbound-email-as-json-nextjs"
            className="font-medium text-primary underline underline-offset-4 hover:no-underline">
            Next.js guide
          </Link>{' '}
          for a full example.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">
          4. Retries and replay
        </h2>
        <p className="leading-relaxed text-muted-foreground">
          If your endpoint returns a non-2xx response, Inbox Bridge retries with
          exponential backoff according to your plan&apos;s retry limit. You can also
          manually replay any delivery from the bridge detail page.
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-2xl font-semibold text-primary">5. Test your integration</h2>
        <p className="leading-relaxed text-muted-foreground">
          Send a test email to your bridge address from any email client. Open the
          bridge&apos;s delivery log to see the request URL, response code, duration,
          and raw payload. Hit replay to test error paths.
        </p>
      </section>

      <div className="flex gap-3">
        <Link
          to={LOGIN_PATH}
          className={cn(buttonVariants({ size: 'lg' }), 'gap-2 font-semibold')}>
          Create your first bridge
          <ArrowRight className="h-4 w-4" />
        </Link>
        <Link
          to="/email-to-webhook"
          className={cn(buttonVariants({ variant: 'outline', size: 'lg' }))}>
          Feature overview
        </Link>
      </div>

      <section className="mt-8 border-t border-border/40 pt-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-primary">
          Related
        </h2>
        <ul className="flex flex-col gap-2 text-base text-muted-foreground">
          <li>
            <Link
              to="/email-to-webhook"
              prefetch="intent"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              Email to webhook overview
            </Link>
            {' — '}what the service does and what payloads look like.
          </li>
          <li>
            <Link
              to="/cloudmailin-alternative"
              prefetch="intent"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              CloudMailin alternative
            </Link>
            {' — '}feature comparison if you&apos;re evaluating providers.
          </li>
        </ul>
      </section>
    </main>
  )
}
